import { nil, NO, UIDialogView, UITextView, UIView, YES } from "uicore-ts"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHEmailRecipientsInputView } from "../Custom components/RH/RHEmailRecipientsInputView"
import { RHClipboard } from "../Custom components/RH/Utils_slop/RHClipboard"
import { RHDialogs } from "../Custom components/RH/Utils_slop/RHDesignTokens"


/**
 * ShareProcurementDialogView — the "Jaga" modal.
 *
 * Everyone can copy the public procurement link (this works). Logged-in procurers
 * additionally get a Google-Docs-style recipient input + "Saada" button; the
 * actual sending is wired by the owning details view via `sendWasRequested`.
 */
export class ShareProcurementDialogView extends UIDialogView {

    readonly contentView = new UIView().configuredWithObject({
        backgroundColor: RHDialogs.share.dialogBackgroundColor,
        style: {
            borderRadius: RHDialogs.share.dialogBorderRadius,
            boxShadow: RHDialogs.share.dialogBoxShadow
        }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, RHDialogs.share.dialogTitleTextViewType)
        .configuredWithObject({
            text: RHDialogs.share.dialogTitle,
            textColor: RHDialogs.share.dialogTitleTextColor
        }).addedAsSubviewToView(this.contentView)

    readonly linkLabel = new UITextView().configuredWithObject({
        textColor: RHDialogs.share.linkFieldTextColor,
        isSingleLine: YES,
        style: {
            fontSize: RHDialogs.share.sectionLabelFontSize,
            border: `1px solid ${RHDialogs.share.dividerColor.stringValue}`,
            borderRadius: RHDialogs.share.inputBorderRadius,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: `${RHDialogs.share.rowHeight}px`,
            paddingLeft: "10px",
            paddingRight: "10px"
        }
    }).addedAsSubviewToView(this.contentView)

    readonly copyLinkButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: RHDialogs.share.copyButtonTitle,
            style: { fontSize: RHDialogs.share.copyButtonFontSize }
        },
        variant: RHDialogs.share.copyButtonVariant,
        contentPadding: RHDialogs.share.copyButtonContentPadding
    }).addedAsSubviewToView(this.contentView)

    // ── Procurer-only send section (added/removed in configure) ────────────────

    readonly sendSectionLabel = new UITextView().configuredWithObject({
        text: RHDialogs.share.sendSectionLabel,
        textColor: RHDialogs.share.sectionLabelTextColor,
        style: { fontSize: RHDialogs.share.sectionLabelFontSize, fontWeight: "600" }
    })

    readonly recipientsInput = new RHEmailRecipientsInputView()

    readonly sendButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: RHDialogs.share.sendButtonTitle,
            style: { fontSize: RHDialogs.share.sendButtonFontSize }
        },
        variant: RHDialogs.share.sendButtonVariant,
        contentPadding: RHDialogs.share.sendButtonContentPadding
    })

    readonly cancelButton = new RHButton().configuredWithObject({
        titleLabel: { text: RHDialogs.share.closeButtonTitle },
        variant: RHDialogs.share.closeButtonVariant
    }).addedAsSubviewToView(this.contentView)

    private _link = ""
    private _canSendToRecipients = NO
    private _isSending = NO

    sendWasRequested?: (recipients: string[]) => Promise<void>


    constructor(elementID?: string) {
        super(elementID)

        this.view = this.contentView
        this.dismissesOnTapOutside = YES

        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.dismiss()
        this.copyLinkButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._copyLink().then(undefined)
        }
        this.sendButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this._sendButtonWasPressed()
        this.recipientsInput.recipientsDidChange = () => this._updateSendButtonAvailability()
    }


    show(link: string, canSendToRecipients: boolean) {
        this._link = link
        this._canSendToRecipients = canSendToRecipients
        this.linkLabel.text = link
        this.copyLinkButton.titleLabel.text = RHDialogs.share.copyButtonTitle
        this._applySendingState(NO)
        this.recipientsInput.clear()

        if (canSendToRecipients) {
            this.sendSectionLabel.addedAsSubviewToView(this.contentView)
            this.recipientsInput.addedAsSubviewToView(this.contentView)
            this.sendButton.addedAsSubviewToView(this.contentView)
        }
        else {
            this.sendSectionLabel.removeFromSuperview()
            this.recipientsInput.removeFromSuperview()
            this.sendButton.removeFromSuperview()
        }
        this._updateSendButtonAvailability()

        this.showInRootView(YES)
        this.setNeedsLayoutUpToRootView()
    }


    private async _copyLink() {
        const didCopy = await RHClipboard.copy(this._link)
        if (!didCopy) {
            return
        }
        this.copyLinkButton.titleLabel.text = RHDialogs.share.copyButtonCopiedTitle
        this.setNeedsLayout()
        this.performFunctionWithDelay(2, () => {
            this.copyLinkButton.titleLabel.text = RHDialogs.share.copyButtonTitle
            this.setNeedsLayout()
        })
    }


    _updateSendButtonAvailability() {
        let enabled = NO
        if (this._isSending) {
            this.sendButton.enabled = NO
            return
        }
        if (this.recipientsInput.recipients().length > 0) {
            enabled = YES
        }
        if (this.recipientsInput.hasValidPendingRecipients()) {
            enabled = YES
        }
        this.sendButton.enabled = enabled
    }


    private _sendButtonWasPressed() {
        if (this._isSending) {
            return
        }
        if (!this.recipientsInput.commitPendingText()) {
            this._updateSendButtonAvailability()
            return
        }

        const recipients = this.recipientsInput.recipients()
        if (recipients.length === 0) {
            this._updateSendButtonAvailability()
            return
        }

        let sendPromise: Promise<void> | undefined
        try {
            sendPromise = this.sendWasRequested?.(recipients)
        }
        catch (error) {
            console.error("ShareProcurementDialogView: sendWasRequested threw", error)
            return
        }
        if (!sendPromise) {
            return
        }

        this._applySendingState(YES)
        sendPromise.then(() => {
            this.dismiss()
        }).catch(error => {
            console.error("ShareProcurementDialogView: share email sending failed", error)
        }).finally(() => {
            this._applySendingState(NO)
        })
    }


    private _applySendingState(isSending: boolean) {
        this._isSending = isSending
        if (isSending) {
            this.sendButton.titleLabel.text = RHDialogs.share.sendButtonSendingTitle
        }
        else {
            this.sendButton.titleLabel.text = RHDialogs.share.sendButtonTitle
        }
        this._updateSendButtonAvailability()
        this.setNeedsLayout()
    }


    // ── Layout ──────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const tokens = RHDialogs.share
        const dialogBounds = this.bounds
        const inset = tokens.dialogContentInset
        const dialogWidth = Math.min(tokens.dialogMaxWidth, dialogBounds.width * tokens.dialogWidthViewportRatio)

        this.contentView.frame = dialogBounds.rectangleWithWidth(dialogWidth, 0.5).rectangleWithHeight(0)
        const bounds = this.contentView.contentBoundsWithInset(inset)

        const headerRowFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)

        // Link row: [ link box (flex) ][ Kopeeri link (fixed) ]
        const linkRowFrame = headerRowFrame
            .rectangleForNextRow(tokens.sectionGap, tokens.rowHeight)
        linkRowFrame.distributeViewsAlongWidth(
            [this.linkLabel, this.copyLinkButton],
            undefined,
            tokens.inputItemGap,
            [nil, tokens.copyButtonWidth]
        )

        let lastRowFrame = linkRowFrame

        if (this._canSendToRecipients) {
            lastRowFrame = linkRowFrame
                .rectangleForNextRow(tokens.sectionGap, this.sendSectionLabel)
                .assignedAsFrameOfView(this.sendSectionLabel)
                .rectangleForNextRow(tokens.inputItemGap, this.recipientsInput)
                .assignedAsFrameOfView(this.recipientsInput)
        }

        const footerRowFrame = lastRowFrame
            .rectangleForNextRow(tokens.sectionGap, tokens.rowHeight)

        if (this._canSendToRecipients) {
            let sendButtonWidth = tokens.sendButtonWidth
            let closeButtonWidth = tokens.closeButtonWidth
            let footerButtonsWidth = sendButtonWidth + closeButtonWidth + tokens.footerButtonGap
            if (footerButtonsWidth > bounds.width) {
                const compactButtonWidth = Math.max(0, (bounds.width - tokens.footerButtonGap) * 0.5)
                sendButtonWidth = compactButtonWidth
                closeButtonWidth = compactButtonWidth
                footerButtonsWidth = bounds.width
            }

            footerRowFrame
                .rectangleWithWidth(footerButtonsWidth, 0.5)
                .distributeViewsAlongWidth(
                    [this.cancelButton, this.sendButton],
                    undefined,
                    tokens.footerButtonGap,
                    [closeButtonWidth, sendButtonWidth]
                )
        }
        else {
            footerRowFrame
                .rectangleWithWidth(tokens.closeButtonWidth, 0.5)
                .assignedAsFrameOfView(this.cancelButton)
        }

        const finalHeight = footerRowFrame.max.y + inset
        let dialogFrame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
        if (dialogFrame.y < tokens.dialogViewportMargin) {
            dialogFrame = dialogFrame.rectangleWithY(tokens.dialogViewportMargin)
        }
        this.contentView.frame = dialogFrame
    }

}
